// @owners { team: patients }
import { type ShipmentTip } from '@alto/scriptdash/alto/orders/types/v1/shipment_tip';
import {
  type CreateShipmentTipRequest,
  type UpdateShipmentTipRequest,
} from '@alto/scriptdash/alto/patients/v3/courier_tips/wunderbar/v1/shipment_tips_endpoint';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import getIsAuthorized from '~shared/features/auth/selectors/getIsAuthorized';
import { setShipmentTips } from '~shared/features/courier-tips/actions/setShipmentTips';
import api from '~shared/features/courier-tips/api';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { staleTimes } from '~shared/queries/constants';
import { invalidateShipmentTips, shipmentTips } from '~shared/queries/query-keys/shipmentTips';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type APIError } from '~shared/types/APIError';

/**
 * Fetch the current patient's shipment tips — the React Query replacement for the deprecated
 * fetchShipmentTipsForUser thunk. Runs only when authorized and a current user id is known.
 */
export const useGetShipmentTips = () => {
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const userID = useSelectorShared(getCurrentUserID);
  const { data, error, isError, isLoading, isSuccess } = useQuery({
    ...shipmentTips.fetchForUser(userID ?? 0),
    enabled: isAuthorized && !!userID,
    staleTime: staleTimes.fifteenMinutes,
  });

  return { shipmentTips: data ?? [], error: error as APIError | null, isError, isLoading, isSuccess };
};

/**
 * Bridge: mirror the React Query shipment-tips data into the Redux `shipmentTips` slice so existing
 * Reselect selectors (getShipmentTipForShipmentID → checkout's getOrder) keep working without a
 * Redux networking action. One-directional (RQ → Redux); the slice is written only by the fetch, so
 * an errored refetch never clobbers it. Mount once at an always-rendered authed root. Transitional
 * bridge toward strategy A — see the redux-thunk-to-react-query-migration skill.
 */
export const useShipmentTipsReduxBridge = () => {
  const dispatch = useDispatchShared();
  const { shipmentTips: tips, isSuccess } = useGetShipmentTips();

  useEffect(() => {
    if (isSuccess) {
      dispatch(setShipmentTips(tips));
    }
  }, [dispatch, isSuccess, tips]);
};

/**
 * Create a shipment tip — React Query replacement for the deprecated createShipmentTip thunk.
 * Invalidates the tips query on success so the bridge re-syncs the full list into Redux.
 */
export const useCreateShipmentTip = () =>
  useMutation({
    mutationFn: async (params: CreateShipmentTipRequest) => {
      const { data, error } = (await api.createShipmentTip(params)) as { data: ShipmentTip; error?: APIError };
      if (error) {
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      void invalidateShipmentTips();
    },
  });

/**
 * Update a shipment tip — React Query replacement for the deprecated updateShipmentTip thunk.
 * Invalidates the tips query on success so the bridge re-syncs the full list into Redux.
 */
export const useUpdateShipmentTip = () =>
  useMutation({
    mutationFn: async (params: UpdateShipmentTipRequest) => {
      const { data, error } = (await api.updateShipmentTip(params)) as { data: ShipmentTip; error?: APIError };
      if (error) {
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      void invalidateShipmentTips();
    },
  });
